import React, { useState, useRef } from "react";
import "./AddressUpload.css";

function AddressUpload({ onBack, onContinue, isSubmitting = false }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (!file) return;

    // Validate file type (images and pdfs are standard for bills)
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      setError("Please upload a valid image (JPEG, PNG) or PDF file.");
      return;
    }

    // Validate size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds the 10MB limit.");
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview if it's an image
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      // For PDFs, show a PDF icon/placeholder
      setPreviewUrl("pdf-placeholder");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleUploadSubmit = () => {
    if (!selectedFile) {
      setError("Please select a file to upload first.");
      return;
    }
    onContinue(selectedFile);
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
  };

  return (
    <div className="address-upload-container">
      <div className="address-upload-card">
        <div className="address-upload-header">
          <button className="back-button" onClick={onBack} disabled={isSubmitting}>
            ←
          </button>
          <div className="header-progress">
            <div
              className="header-progress-fill"
              style={{ width: "90%" }}
            ></div>
          </div>
        </div>

        <div>
          <h2 className="address-upload-title">Upload Address Document</h2>
          <p className="address-upload-subtitle">
            Upload a clear photo or copy of your document (max 10MB)
          </p>

          {error && <div className="upload-error-message">{error}</div>}

          {!selectedFile ? (
            <div
              className="dropzone"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={triggerFileInput}
            >
              <svg
                className="upload-cloud-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="dropzone-text">
                Drag and drop your file here, or <span>browse</span>
              </p>
              <p className="dropzone-subtext">Supports JPG, PNG, PDF</p>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept="image/jpeg,image/png,image/jpg,application/pdf"
              />
            </div>
          ) : (
            <div className="preview-container">
              {previewUrl === "pdf-placeholder" ? (
                <div className="pdf-preview">
                  <svg
                    className="pdf-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <p className="pdf-name">{selectedFile.name}</p>
                </div>
              ) : (
                <div className="image-preview-wrapper">
                  <img src={previewUrl} alt="Preview" className="image-preview" />
                </div>
              )}
              <button className="clear-btn" onClick={handleClear} disabled={isSubmitting}>
                Remove file
              </button>
            </div>
          )}

          <div className="privacy-notice">
            <svg
              className="lock-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
            <span>Your document is encrypted and secure.</span>
          </div>
        </div>

        <div>
          <button
            className="continue-button"
            onClick={handleUploadSubmit}
            disabled={!selectedFile || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="btn-spinner"></span>
                Uploading...
              </>
            ) : (
              "Submit Document"
            )}
          </button>
          <div className="address-upload-footer">Secured by VerifiLite</div>
        </div>
      </div>
    </div>
  );
}

export default AddressUpload;
